import React, { useState, useEffect, useRef } from "react";
import cx from "classnames";
import { FileDownloadOutlined } from "@mui/icons-material";
import DownloadErrors from "./DownloadErrors";
import DownloadReceipt from "./DownloadReceipt";
import FileIcon from "./shared/FileIcon";
import buildSummaryData from "./shared/buildSummaryData";
import { wholeNumberWithCommas } from "../utils/NumberFormatter";
import useOutsideClick from "../Hooks/useOutsideClick";

const ReviewSubmissionModal = (props) => {
  const [submitting, setSubmitting] = useState(false);
  const summaryData = buildSummaryData(props.data);
  const invalidRows = summaryData.rows - summaryData.valid;

  const modalRef = useRef();
  useOutsideClick(modalRef, () => props.setModalState(false));

  const handleSubmitting = () => {
    setSubmitting(true);

    if (submitting === false) {
      props.action();
    }
  };

  const pluralize = (num, str) => {
    if (Number(num) === 1) {
      return str;
    } else {
      return str + "s";
    }
  };

  const renderErrorsWarning = () => {
    if (invalidRows > 0) {
      return (
        <div className="review_modal__tile">
          <div className="flex justify-content-between align-items-center">
            <div className="file_stats">
              <div className="file_stats__img file_stats__img--xs">
                <FileIcon text="CSV" />
              </div>
              <div className="file_stats__text">
                <div className="review_modal__file__title">Validation Alert Rows</div>
                <div className="review_modal__file__subtitle">
                  {`${wholeNumberWithCommas(invalidRows)} ${pluralize(invalidRows, "row")}`}
                </div>
              </div>
            </div>
            <DownloadErrors
              submissionType={props.submissionType}
              attachments={props.data}
              className="button button--white button--sq-lg"
              label={<FileDownloadOutlined style={{ fontSize: "20px" }} />}
            />
          </div>
          <div className="flex--col gap--12 pt--16">
            <div className="t--xsm t--subtitle">
              Your submission contains{" "}
              {`${wholeNumberWithCommas(invalidRows)} ${pluralize(invalidRows, "row")}`} with
              validation alerts. {invalidRows === 1 ? "This" : "These"}{" "}
              {pluralize(invalidRows, "row")} will not be processed in this submission.
            </div>
            <div className="t--xsm t--orange t--500">
              Download the excluded {pluralize(invalidRows, "row")} so that you can submit{" "}
              {invalidRows === 1 ? "it" : "them"} at a later date.
            </div>
          </div>
        </div>
      );
    }
  };

  const renderReceiptDownload = () => {
    if (summaryData.valid === 0) return null;

    return (
      <div className="review_modal__tile">
        <div className="flex justify-content-between align-items-center">
          <div className="file_stats">
            <div className="file_stats__img file_stats__img--xs">
              <FileIcon text="CSV" />
            </div>
            <div className="file_stats__text">
              <div className="review_modal__file__title">Submission Data</div>
              <div className="review_modal__file__subtitle">
                {`${wholeNumberWithCommas(summaryData.valid)} ${pluralize(summaryData.valid, "row")}`}
              </div>
            </div>
          </div>
          <DownloadReceipt
            submissionType={props.submissionType}
            submissionName={props.submissionName}
            attachments={props.data}
            className="button button--white button--sq-lg"
            label={<FileDownloadOutlined style={{ fontSize: "20px" }} />}
          />
        </div>
        <div className="t--xsm t--subtitle pt--16">
          We assign an ESP ID to each claim for your reference. This file contains your raw data
          with the ESP ID appended. The data will be de-identified after you submit.
        </div>
      </div>
    );
  };

  const renderInfoTable = () => {
    return (
      <div className="review_modal__table">
        <div className="review_modal__table__row">
          <span className="review_modal__table__header t--break">{props.submissionName}</span>
        </div>
        <div className="review_modal__table__row">
          <div>Files Attached</div>
          <div>{summaryData.files}</div>
        </div>
        <div className="review_modal__table__row">
          <div>No. Covered Entities</div>
          <div>{summaryData.entities.length}</div>
        </div>
        <div className="review_modal__table__row">
          <div>No. Manufacturers</div>
          <div>{summaryData.manufacturers}</div>
        </div>
        <div className="review_modal__table__row">
          <div>Rows for Submission</div>
          <div>{wholeNumberWithCommas(summaryData.valid)}</div>
        </div>
      </div>
    );
  };

  const renderModal = () => {
    if (props.displayModal) {
      return (
        <div className="tpa-modal__background">
          <div className="tpa-modal__container" ref={modalRef}>
            <div className="tpa-modal__body">
              <div className="flex--col">
                <div className="t--xl t--500">Review and Submit</div>
                <div className="t--navyblue t--sm pt--8 pb--16">
                  Please confirm that you have verified the attached files and that your column
                  mappings are correct.
                </div>
                {renderInfoTable()}
                <div className="review_modal__downloads__header t--16 t--700">Downloads</div>
                <div className="t--navyblue t--sm pt--8 pb--8">
                  For your convenience, download before submitting.
                </div>
                <div className="flex--col gap--8">
                  {renderReceiptDownload()}
                  {renderErrorsWarning()}
                </div>
              </div>
              <div className="flex justify-content-end gap--16">
                <button
                  className="button button--lg button--white"
                  onClick={() => {
                    props.setModalState(false);
                  }}
                >
                  Cancel
                </button>
                <button
                  className="button button--lg button--blue"
                  id="confirmButton"
                  onClick={() => {
                    handleSubmitting();
                  }}
                >
                  {submitting ? <div className="loading-indicator"></div> : "Confirm & Submit"}
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    } else {
      return null;
    }
  };

  return renderModal();
};
export default ReviewSubmissionModal;
